import { faPen } from '@fortawesome/pro-light-svg-icons';
import { faSignalStream } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { UpdateLiveStreamForm } from '~/components/forms/LiveStreamForm';
import { Breadcrumbs } from '~/components/molecules/Breadcrumbs';
import { useNavigate } from '~/components/utils/Navigation';
import { useLiveStreamQuery } from '~/lib/api/modules/LiveStreams/hooks/useLiveStreamQuery';
import { QuerySkeleton } from '../atoms/QuerySkeleton';
import { Heading } from '../molecules/Heading';
import { AppBreadcrumbs } from '../organisms/Breadcrumbs';

export type IAppUpdateLiveStreamPageProps = {
  appId: string;
  liveStreamId: string;
  nextUrl?: string;
  cancelUrl?: string;
};

export const AppUpdateLiveStreamPage: React.FC<
  IAppUpdateLiveStreamPageProps
> = (props) => {
  const { appId, liveStreamId, nextUrl, cancelUrl } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const liveStreamQuery = useLiveStreamQuery({
    appId,
    liveStreamId,
  });

  const parentUrl = `/apps/${appId}/liveStreams/${liveStreamId}`;

  return (
    <>
      <Helmet>
        <title>{t('AppUpdateLiveStreamPage.seo.title')}</title>
      </Helmet>

      <AppBreadcrumbs appId={appId}>
        <Breadcrumbs.Action href={`/apps/${appId}/liveStreams`}>
          <FontAwesomeIcon icon={faSignalStream} />
          {t('AppLiveStreamsPage.title')}
        </Breadcrumbs.Action>
        <Breadcrumbs.Action href={parentUrl}>
          <QuerySkeleton query={liveStreamQuery} length={{ min: 8, max: 16 }}>
            {liveStreamQuery.data?.displayName}
          </QuerySkeleton>
        </Breadcrumbs.Action>
        <Breadcrumbs.Text current>
          <FontAwesomeIcon icon={faPen} />
          {t('common.editing')}
        </Breadcrumbs.Text>
      </AppBreadcrumbs>

      <Heading
        icon={<FontAwesomeIcon icon={faSignalStream} />}
        title={
          <QuerySkeleton query={liveStreamQuery} length={{ min: 8, max: 16 }}>
            {t('AppUpdateLiveStreamPage.title', {
              name: liveStreamQuery.data?.displayName,
            })}
          </QuerySkeleton>
        }
        previousUrl={parentUrl}
      />

      <UpdateLiveStreamForm
        appId={appId}
        liveStreamId={liveStreamId}
        onSuccess={() => navigate(nextUrl ?? parentUrl)}
        cancelUrl={cancelUrl ?? parentUrl}
      />
    </>
  );
};
